import Head from 'next/head';
import Layout from '@layout/index';
import { NextSeo } from 'next-seo';
import Landing from '@cont/Compete/Landing/Landing';
import {
  fetchCompeteInfo,
  fetchLeaderboard,
  fetchLeaderboardFilters,
  fetchRatingInfo
} from '@api/fetchers/compete';
import { SWRConfig } from 'swr';
import { useRouter } from 'next/router';
import {
  getCompeteInfoKey,
  getCompeteRatingKey,
  getLeaderboardFiltersKey,
  isCompeteEnabled
} from '@cont/Compete/utils';
import { fetchConstants } from '@api/fetchers/constants';
import { useSetSelectedNav } from '@stores/sidenav';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import useTopology from '@api/hooks/topology/useTopology';

const Compete = ({ fallbackData = {} }) => {
  const { query } = useRouter();
  const { goalUID } = query;
  const leaderboardFiltersKey = getLeaderboardFiltersKey(goalUID);

  const { topology } = useTopology(goalUID);
  const metaTitle = `Compete | ${topology?.title} | Unacademy`;

  const competeRatingKey = getCompeteRatingKey(goalUID);
  const competeInfoKey = getCompeteInfoKey(goalUID);
  useSetSelectedNav('compete');

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [leaderboardFiltersKey]: fallbackData.leaderboardFilters,
          [competeRatingKey]: fallbackData.competeRatingData,
          [competeInfoKey]: fallbackData.competeInfoData
        }
      }}
    >
      <NextSeo
        title={metaTitle}
        openGraph={{
          title: metaTitle
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <Landing fallback={fallbackData.leaderboard} />
      <OneLinkSmartScript />
    </SWRConfig>
  );
};

Compete.pageInfo = {
  isMWebSupported: true,
  name: 'Compete page',
  layoutOptions: {
    hideFooter: true,
    showPlusNav: true
  },
  isCMSMeta: true
};

export const getStaticProps = async ({ params: query }) => {
  const { goalUID } = query;

  const constants = await fetchConstants(goalUID);

  const isCompeteGoal = isCompeteEnabled(goalUID, constants);

  if (!isCompeteGoal) {
    return { notFound: true, revalidate: 60 };
  }
  const promises = [
    fetchLeaderboardFilters(goalUID),
    fetchLeaderboard(goalUID),
    fetchRatingInfo(goalUID),
    fetchCompeteInfo(goalUID)
  ];

  try {
    const [
      leaderboardFilters,
      leaderboard,
      competeRatingData,
      competeInfoData
    ] = await Promise.all(promises);

    return {
      props: {
        fallbackData: {
          leaderboardFilters,
          leaderboard,
          competeRatingData,
          competeInfoData
        }
      }
    };
  } catch {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default Compete;
Compete.Layout = Layout;
